import * as c from 'models/constants';
import {
	BookingListItemDto,
	IAddBookingItemRequest,
	IBookAppointment,
	IBookAppointmentAction,
	IBookingSearchFilters,
	IFormAddBookingItemRequest,
} from 'models/bookappointment';
import { Dispatch } from 'react-redux';
import { requestHelper } from 'redux/helpers';
import { IStore } from 'redux/IStore';
import { destroy } from 'redux-form';
import { addBookingToCart, clearShoppingBasket, createOrder, getStripeSession } from '../cart';
import { IUserPreferences } from 'models/user/userpreferences';
import { getUserShippingAddresses } from '../user/shipping/usershippingaddresses';
import { IOrderRequest } from 'models/orders/order';
import { browserHistory } from 'react-router';
import { groupBy } from 'containers/Shop/Helpers';

/** Action Types */
export const REQUEST_START: string = 'bookappointment/REQUEST_START';
export const REQUEST_FAILURE: string = 'bookappointment/REQUEST_FAILURE';
export const REQUEST_SUCCESS: string = 'bookappointment/REQUEST_SUCCESS';
export const REQUEST_ITEMS_SUCCESS: string = 'bookappointment/REQUEST_ITEMS_SUCCESS';
export const REQUEST_IDX_ITEMS_SUCCESS: string = 'bookappointment/REQUEST_IDX_ITEMS_SUCCESS';
export const CLEAR_FORM_DATA: string = 'bookappointment/CLEAR_FORM_DATA';
export const RESET_SEARCHES: string = 'bookappointment/RESET_SEARCHES';
export const SET_STEP: string = 'bookappointment/SET_STEP';
export const SET_FINAL_STEP: string = 'bookappointment/SET_FINAL_STEP';
export const NEXT_STEP: string = 'bookappointment/NEXT_STEP';
export const PREVIOUS_STEP: string = 'bookappointment/PREVIOUS_STEP';

/** Initial State */
const initialState: IBookAppointment = {
	wizardStep: 0,
	finalStep: 3,
	/**
	 * "indexed" meaning each search result is saved to an index. Allowing multiple searches for separate items.
	 */
	indexedSearches: {},
};

/** Reducer */
export function bookAppointmentReducer(state = initialState, action: IBookAppointmentAction): IBookAppointment {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				error: false,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				message: action.payload.message,
				error: true,
			};

		case REQUEST_SUCCESS:
			return {
				...state,
				isFetching: false,
				message: null,
				error: false,
			};

		case REQUEST_ITEMS_SUCCESS:
			return {
				...state,
				isFetching: false,
				availableItems: action.payload.availableItems,
				error: false,
			};
		case REQUEST_IDX_ITEMS_SUCCESS:
			return {
				...state,
				isFetching: false,
				indexedSearches: Object.assign({}, state.indexedSearches, {
					[action.payload.idx]: action.payload.availableItems,
				}),
				error: false,
			};

		case CLEAR_FORM_DATA:
			return {
				...state,
				error: false,
				wizardStep: 0,
			};

		case RESET_SEARCHES:
			return {
				...state,
				indexedSearches: {},
			};

		case SET_STEP:
			return {
				...state,
				wizardStep: action.payload.wizardStep || 0,
			};
		case SET_FINAL_STEP:
			return {
				...state,
				finalStep: action.payload.finalStep,
			};

		case NEXT_STEP:
			return {
				...state,
				wizardStep: state.wizardStep + 1,
			};

		case PREVIOUS_STEP:
			return {
				...state,
				wizardStep: Math.max(0, state.wizardStep - 1),
			};

		default:
			return state;
	}
}

/** Action Creator */
export function requestStart(): IBookAppointmentAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestSuccess(): IBookAppointmentAction {
	return {
		type: REQUEST_SUCCESS,
	};
}

/** Action Creator */
export function requestFailure(message: any): IBookAppointmentAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function requestItemsSuccess(availableItems: BookingListItemDto[]): IBookAppointmentAction {
	return {
		type: REQUEST_ITEMS_SUCCESS,
		payload: {
			availableItems,
		},
	};
}

/** Action Creator */
export function idxRequestItemsSuccess(idx: number, availableItems: BookingListItemDto[]): IBookAppointmentAction {
	return {
		type: REQUEST_IDX_ITEMS_SUCCESS,
		payload: {
			idx,
			availableItems,
		},
	};
}

/** Action Creator */
export function clearFormData(): IBookAppointmentAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

export function resetIndexedSearches(): IBookAppointmentAction {
	return {
		type: RESET_SEARCHES,
	};
}

/** Action Creator */
export function nextStep(): IBookAppointmentAction {
	return {
		type: NEXT_STEP,
	};
}

/** Action Creator */
export function previousStep(): IBookAppointmentAction {
	return {
		type: PREVIOUS_STEP,
	};
}

/** Action Creator */
export function setStep(step?: number): IBookAppointmentAction {
	return {
		type: SET_STEP,
		payload: {
			wizardStep: step || 0,
		},
	};
}

export function setFinalStep(step: number): IBookAppointmentAction {
	return {
		type: SET_FINAL_STEP,
		payload: {
			finalStep: step || 0,
		},
	};
}

interface BookingRequest {
	bookingTemplateId: string;
	resourceId: string;
	childrenGenerateBookingListForBookingTemplateRequests: { item1: string; item2: string };
}
type AndOrBookingRequest = Array<Array<BookingRequest>>;

/**
 * input:
 * [
 *  [BT1, BT2, BT3],
 *  [BT4],
 *  [BT5, BT6]
 * ]
 *
 * output:
 * [
 *  [BT1, BT4, BT5],
 *  [BT1, BT4, BT6],
 *  [BT2, BT4, BT5],
 *  [BT2, BT4, BT6],
 *  [BT3, BT4, BT5],
 *  [BT3, BT4, BT6],
 * ]
 *
 */
const generateAllCombinations: <T>(request: Array<Array<T>>, i?: number) => Array<Array<T>> = (request, i = 0) => {
	if (i == request.length - 1) {
		return request[i].map((x) => [x]);
	}
	const out = [];
	for (const req of request[i]) {
		for (const arr of generateAllCombinations(request, i + 1)) {
			arr.unshift(req);
			out.push(arr);
		}
	}
	return out;
};

const numberOfMaxMatchingResourceIds = (item: BookingListItemDto) => {
	const countByRes: { [key: string]: number } = item.availableBookings.reduce(
		(prev, curr) => ({ ...prev, [curr.resourceId]: (prev[curr.resourceId] || 0) + 1 }),
		{},
	);
	return Object.keys(countByRes).reduce((prev, curr) => Math.max(prev, countByRes[curr]), 0);
};

/**
 * find biggest element by the given metric
 * @param arr
 * @param metric how to judge element size (if array judge first by the first metric, then second, ...)
 */
const findMax: <T>(arr: T[], metric: ((item: T) => number) | Array<(item: T) => number>) => T = (arr, metric) => {
	const metrics = metric instanceof Array ? metric : [metric];
	return arr.reduce((prev, curr) => {
		if (!prev) {
			return curr;
		}

		let bigger = false;
		for (const m of metrics) {
			const currNum = m(curr);
			const prevNum = m(prev);

			if (currNum != prevNum) {
				if (currNum > prevNum) {
					bigger = true;
				}
				break;
			}
		}
		if (bigger) {
			return curr;
		}
		return prev;
	}, undefined);
};

/** Action Creator */
export function searchAvailableBookings(
	requests: IBookingSearchFilters[],
	includePartiallyUnavailableResources: boolean = false,
) {
	//TODO - seems to be a bit buggy when setting time only => research needed
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		const arrayServiceId = [];
		const timeArray = [];
		const bodyArray = [];
		const arrayOfResponses = [];

		const serviceIdByVp = {};
		const orderOfVp = [];

		requests.forEach((r) => {
			const id = r.serviceId;
			const resourceId = r?.resourceId;

			// childrenGenerateBookingListForBookingTemplateRequests is used for bundles
			if (arrayServiceId.findIndex((x) => x.bookingTemplateId === id && x.resourceId === resourceId) == -1) {
				const alll = {
					bookingTemplateId: id,
					resourceId: resourceId,
					childrenGenerateBookingListForBookingTemplateRequests: r.childrenBookingTemplates,
				};
				arrayServiceId.push(alll);
				(serviceIdByVp[r.variantParentId] = serviceIdByVp[r.variantParentId] || []).push(alll);

				if (orderOfVp.findIndex((x) => x == r.variantParentId) == -1) {
					orderOfVp.push(r.variantParentId);
				}
			}
		});

		/* 
    Convert to algebraic normal form:
    [
      [BT0-var1 OR BT0-var2]
      AND BT1
      AND BT2
      AND [BT3-var1,BT3-var2]
    ]

    gets converted into

    [
      [BT0-var1 AND BT1 AND BT2 AND BT3-var1] OR
      [BT0-var1 AND BT1 AND BT2 AND BT3-var2] OR
      [BT0-var2 AND BT1 AND BT2 AND BT3-var1] OR
      [BT0-var2 AND BT1 AND BT2 AND BT3-var2]
    ]
    */
		const andOrBookingRequest: AndOrBookingRequest = orderOfVp.map((vp) => serviceIdByVp[vp]);
		const allCombinations = generateAllCombinations(andOrBookingRequest);

		requests.forEach((req) => {
			timeArray.push({
				start: req.dateFrom,
				end: req.dateTo,
			});
		});

		const filteredTimeArray = [];
		timeArray.forEach((item) => {
			const i = filteredTimeArray.findIndex((x) => x.start === item.start && x.end === item.end);
			if (i <= -1) {
				filteredTimeArray.push(item);
			}
		});

		allCombinations.forEach((btRequestPath) => {
			filteredTimeArray.forEach((r) => {
				const body = {
					start: r.start,
					end: r.end,
					generateBookingListForBookingTemplateRequests: btRequestPath,
					includePartiallyUnavailableResources,
				};
				bodyArray.push(body);
			});
		});

		dispatch(requestStart());
		return Promise.all(
			bodyArray.map((body) => {
				return requestHelper<BookingListItemDto[]>(
					fetch(
						getState().fetchDefault.baseAPI + 'api/Shop/GetAvailableBookings',
						Object.assign({}, getState().fetchDefault.requestInit, {
							method: 'POST',
							body: JSON.stringify(body),
						}),
					),
				)
					.then((res) => {
						arrayOfResponses.push(res);
					})
					.catch((e) => {
						console.warn(e);
						dispatch(requestFailure(e));
					});
			}),
		)
			.then(() => {
				const res: BookingListItemDto[] = [].concat.apply([], arrayOfResponses);
				const groupBySchedule = groupBy(res, 'schedule');
				const schedules = Object.keys(groupBySchedule);
				schedules.sort();
				const out = schedules.map(
					(sc) =>
						findMax<BookingListItemDto>(groupBySchedule[sc], [
							(x) => numberOfMaxMatchingResourceIds(x), // find the element with the max number of matching resources
							(x) => -x.price,
						]), // then by smallest price
				);
				dispatch(requestItemsSuccess(out));
			})
			.catch((e) => {
				console.warn(e);
				dispatch(requestFailure(e));
			}); //TODO: a lot of testing, problematic: variants :¨) lovely
	};
}

/**
 * Reschedule order needs multiple searches on one page
 * @param requests
 * @returns
 */
export function searchAvailableBookingsIndexed(
	index: number,
	requests: IBookingSearchFilters[],
	ignoredBookings?: Array<string>,
	includePartiallyUnavailableResources: boolean = true,
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const arrayServiceId = [];
		requests.map((r) => {
			const id = r.serviceId;
			const resourceId = r?.resourceId;
			// childrenGenerateBookingListForBookingTemplateRequests is used for bundles
			arrayServiceId.push({
				bookingTemplateId: id,
				resourceId: resourceId,
				childrenGenerateBookingListForBookingTemplateRequests: r.childrenBookingTemplates,
			});
		});
		const body = {
			start: requests[0].dateFrom,
			end: requests[0].dateTo,
			generateBookingListForBookingTemplateRequests: arrayServiceId,
			ignoredBookings,
			includePartiallyUnavailableResources,
			bookingInterval: 60 * 5, //When searching manual bookings use 5min iterval
		};
		dispatch(requestStart());
		return requestHelper<BookingListItemDto[]>(
			fetch(
				getState().fetchDefault.baseAPI + 'api/Shop/GetAvailableBookings',
				Object.assign({}, getState().fetchDefault.requestInit, {
					method: 'POST',
					body: JSON.stringify(body),
				}),
			),
		)
			.then((ress) => {
				const res: BookingListItemDto[] = [].concat.apply([], ress);
				res.sort((a, b) => (b.schedule > a.schedule ? -1 : 1));
				dispatch(idxRequestItemsSuccess(index, res));
			})
			.catch((e) => {
				console.warn(e);
				dispatch(requestFailure(e));
			});
	};
}

/**
 *
 */
export function getBookingAvailability(bookingItems: IFormAddBookingItemRequest[]) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		return requestHelper(
			fetch(getState().fetchDefault.baseAPI + 'api/Shop/GetBookingsAvailability', {
				...getState().fetchDefault.requestInit,
				method: 'POST',
				body: JSON.stringify(bookingItems),
			}),
		)
			.then((res) => {
				dispatch(requestSuccess());
				return { message: 'Booking availability fetched successfully', status: 'ok', result: res };
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function addBookingToShoppingBasket(
	request: IAddBookingItemRequest,
	customerName?: string,
	customerPhone?: string,
	customerCallingCodeKey?: number,
	currencyKey?: number,
	selectedPayment?: string,
	shopCode?: string,
	restrictedMode?: boolean,
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());
		// Clear shopping basket before request for now, change after Marketplace is implemented
		return dispatch(clearShoppingBasket(false))
			.then(() => dispatch(addBookingToCart(request)))
			.then((res) => {
				if (res.ok || res) {
					const getAdresses = dispatch(getUserShippingAddresses());
					return getAdresses.then(() => {
						const userAddresses = getState().userShippingAddresses?.items || [];
						let address = userAddresses.find((a) => a.isDefault);
						if (!address && userAddresses.length) {
							address = userAddresses[0];
						}
						if (!address) {
							address = {};
						}
						const userPreferences: Partial<IUserPreferences> = getState().userPreferences || {};
						const name =
							customerName ||
							userPreferences.name ||
							userPreferences.givenName + ' ' + userPreferences.familyName ||
							address.name ||
							'Name';
						const phoneNumber = customerPhone || userPreferences.phoneNumber || address.phoneNumber || '000000';
						const callingCodeKey =
							customerCallingCodeKey || address.callingCodeKey || userPreferences.callingCodeKey || 208; // 208 = '+386'
						const orderRequest: IOrderRequest = {
							email: userPreferences.email || undefined,
							shippingName: name,
							shippingAddressLine: address.addressLine1 || undefined,
							shippingZIPCode: address.zipCode || undefined,
							shippingCity: address.city || undefined,
							shippingState: address.state || undefined,
							shippingCountryKey: address.countryKey || undefined, //Slovenia
							shippingPhoneNumber: phoneNumber,
							shippingCallingCodeKey: callingCodeKey,
							shippingNote: undefined,
							shippingPicture: userPreferences?.profilePicture || undefined,

							billingName: name,
							billingAddressLine: address.addressLine1 || undefined,
							billingZIPCode: address.zipCode || undefined,
							billingCity: address.city || undefined,
							billingState: address.state || undefined,
							billingCountryKey: address.countryKey || undefined, //Slovenia

							currencyKey: currencyKey,
						};

						return dispatch(createOrder(orderRequest, 'normal')).then(() => {
							//now newORder is in cart reducer call stripe session with newORder Id
							const createdOrder = getState().cart.createdOrder;
							if (!createdOrder) {
								throw 'Create order failure!';
							}
							dispatch(requestSuccess());
							dispatch(destroy(c.FORM_NAME_CREATE_SHOP));
							const cartthankyouurl = `/cartthankyou?order_id=${createdOrder.id}${
								restrictedMode ? `&${c.RESTRICTED_MODE}=1` : ''
							}`;
							const shopUrl = shopCode ? (restrictedMode ? '/myShop/' : '/shop/') + shopCode : '/home';

							if (selectedPayment === 'payNow') {
								const successUrl: string = getState().fetchDefault.baseUrlClient + cartthankyouurl;
								const cancelUrl: string = getState().fetchDefault.baseUrlClient + shopUrl;
								dispatch(getStripeSession(createdOrder.id, successUrl, cancelUrl));
							} else {
								browserHistory.push(cartthankyouurl);
							}
							// dispatch(setStep(getState().bookAppointment.finalStep + 1 || 4));
						});
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							throw res;
						})
						.catch(() => {
							const message: any = { status, statusText };
							throw message;
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				dispatch(clearShoppingBasket(false));
			});
	};
}
